import {useEffect, useRef, useState} from 'react';
import {useLatest} from 'ahooks';
import {sendPrompt} from '@Components/ai-chat-window/lib/ai-chat-prompt.library';
import {useAppSelector} from '@/hooks';
import {useAiChatState} from '@AiChat/providers/ai-chat.context';
import {useAiChatMessage} from '@Components/ai-chat-window/providers/ai-chat-message.context';
import type {ComponentAction, ComponentActionEntries, ComponentActionSession, ComponentActionTarget} from './component-action.types';
import {isPromptSentAfterMessage} from './component-action.library';

export function useComponentActionEntries(action: ComponentAction | undefined, targets: ComponentActionTarget[]): ComponentActionEntries {
  const {chatId, messageId} = useAiChatMessage();
  const promptBoxIdRef = useLatest(useAiChatState().promptBoxId);
  const messages = useAppSelector((state) => state.aiChatWindow[chatId]?.messages);
  const [targetIdsResumedHere, setTargetIdsResumedHere] = useState<string[]>([]);
  const resumedTargetIdsRef = useRef(new Set<string>());
  const sessionRef = useRef<ComponentActionSession | null>(null);

  useEffect((): (() => void) | undefined => {
    if (action === undefined) {
      return undefined;
    }

    const resumeChatFor = (target: ComponentActionTarget): void => {
      // An action may report an entry done more than once; a second resume would post a duplicate turn.
      if (resumedTargetIdsRef.current.has(target.id)) {
        return;
      }

      resumedTargetIdsRef.current.add(target.id);
      setTargetIdsResumedHere((targetIds) => [...targetIds, target.id]);
      sendPrompt(promptBoxIdRef.current, action.resumePromptFor(target));
    };

    const session = action.openSession(resumeChatFor);
    sessionRef.current = session;

    return (): void => {
      session.close();
      sessionRef.current = null;
    };
  }, [action]);

  const isDoneInTranscript = (target: ComponentActionTarget): boolean => {
    return action !== undefined && messages !== undefined && isPromptSentAfterMessage(messages, messageId, action.resumePromptFor(target));
  };

  const doneTargetIds = targets.filter((target) => targetIdsResumedHere.includes(target.id) || isDoneInTranscript(target)).map((target) => target.id);

  const startAction = (target: ComponentActionTarget): void => {
    sessionRef.current?.start(target);
  };

  return {doneTargetIds, startAction};
}
